import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Bars3Icon,
  HomeIcon,
  UserGroupIcon,
  Cog6ToothIcon,
  XMarkIcon,
  GlobeAltIcon,
  DocumentTextIcon,
  RocketLaunchIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';



const navigation = [
  { name: 'Dashboard', href: '/leads', icon: HomeIcon },
  { name: 'Research Ideal Customer', href: '/research', icon: UserGroupIcon },
  { name: 'AI Agent', href: '/ai-agent', icon: CpuChipIcon },
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function CompanySettingsModal() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // ── State ──
  const [companyForm, setCompanyForm] = useState({
    Email: '',
    Telephone: '',
    CompanyName: '',
    Industry: '',
    Website: '',
    ContactName: '',
    Address: '',
    Postcode: '',
    Description: '',
    ValueProposition: '',
    CallToAction: '',
    EmailFooter: '',
  });
  const [savedEmailFooterHtml, setSavedEmailFooterHtml] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsFeedback, setSettingsFeedback] = useState('');
  const [settingsError, setSettingsError] = useState('');
  const [passwordOne, setPasswordOne] = useState('');
  const [passwordTwo, setPasswordTwo] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordFeedback, setPasswordFeedback] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [readWebsiteLoading, setReadWebsiteLoading] = useState(false);

  // ── Helper: parse JSON safely ──
  const parseJsonOrThrow = async (response, endpointLabel) => {
    const contentType = String(response.headers.get('content-type') || '').toLowerCase();
    const raw = await response.text();
    if (!raw) return null;
    if (!contentType.includes('application/json')) {
      const preview = raw.slice(0, 140).replace(/\s+/g, ' ').trim();
      throw new Error(`${endpointLabel} returned non-JSON (${response.status}). ${preview}`);
    }
    try {
      return JSON.parse(raw);
    } catch (jsonError) {
      throw new Error(`${endpointLabel} returned invalid JSON (${response.status}).`);
    }
  };

  // ── API: Fetch company profile ──
  const fetchCompanyProfile = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/company', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch company profile');

      const payload = await parseJsonOrThrow(response, '/leads/company');
      const row = payload?.data?.[0] || payload?.data || {};

      setCompanyForm({
        Email: String(row.Email ?? ''),
        Telephone: String(row.Telephone ?? ''),
        CompanyName: String(row.CompanyName ?? ''),
        Industry: String(row.Industry ?? ''),
        Website: String(row.Website ?? ''),
        ContactName: String(row.ContactName ?? ''),
        Address: String(row.Address ?? ''),
        Postcode: String(row.Postcode ?? ''),
        Description: String(row.Description ?? ''),
        ValueProposition: String(row.ValueProposition ?? ''),
        CallToAction: String(row.CallToAction ?? ''),
        EmailFooter: String(row.EmailFooter ?? ''),
      });
      setSavedEmailFooterHtml(String(row.EmailFooter ?? ''));
    } catch (err) {
      console.error('Failed to fetch company profile:', err);
      setSettingsError('Unable to load company settings.');
    }
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  // ── Handlers ──
  const handleCompanyFieldChange = (field, value) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  // ── API: Read Website ──
  const handleReadWebsite = async () => {
    if (!companyForm.Website) {
      setSettingsError('Please enter a website URL first.');
      return;
    }

    setReadWebsiteLoading(true);
    setSettingsError('');
    setSettingsFeedback('');

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/readwebsite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ website: companyForm.Website }),
      });

      const payload = await parseJsonOrThrow(response, '/leads/readwebsite');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to read website');
      }

      if (payload.data) {
        setCompanyForm((prev) => ({
          ...prev,
          Website: payload.data.Website || prev.Website,
          Description: payload.data.Description || prev.Description,
          ValueProposition: payload.data.ValueProposition || prev.ValueProposition,
          CallToAction: payload.data.CallToAction || prev.CallToAction,
        }));
      }
      setSettingsFeedback('Website analysed. Your AI-written sales profile has been saved.');
    } catch (err) {
      console.error(err);
      setSettingsError(err.message || 'Unable to read website.');
    } finally {
      setReadWebsiteLoading(false);
    }
  };

  // ── API: Save company profile ──
  const saveCompanyProfile = async () => {
    setSettingsSaving(true);
    setSettingsError('');
    setSettingsFeedback('');

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/editcompany', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(companyForm),
      });

      const payload = await parseJsonOrThrow(response, '/leads/editcompany');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to update company profile');
      }

      setSettingsFeedback('Company details updated.');
      setSavedEmailFooterHtml(companyForm.EmailFooter || '');
    } catch (err) {
      console.error(err);
      setSettingsError(err.message || 'Unable to update company details.');
    } finally {
      setSettingsSaving(false);
    }
  };

  // ── API: Change password ──
  const savePasswordChange = async () => {
    setPasswordError('');
    setPasswordFeedback('');

    if (!passwordOne || !passwordTwo) {
      setPasswordError('Please enter both password fields.');
      return;
    }

    if (passwordOne !== passwordTwo) {
      setPasswordError('Passwords must match.');
      return;
    }

    setPasswordSaving(true);

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/passwordchange', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ password: passwordOne, confirmPassword: passwordTwo }),
      });

      const payload = await parseJsonOrThrow(response, '/leads/passwordchange');
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || 'Unable to change password');
      }

      setPasswordFeedback('Password changed successfully.');
      setPasswordOne('');
      setPasswordTwo('');
    } catch (err) {
      console.error(err);
      setPasswordError(err.message || 'Unable to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleClose = () => {
    navigate('/leads');
  };

  // ── Render ──
  const savedPreviewHtml = String(savedEmailFooterHtml || '').trim();
  const livePreviewHtml = String(companyForm?.EmailFooter || '').trim();

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile Sidebar */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 left-4 z-50 rounded-lg bg-white p-2 shadow-lg dark:bg-gray-800"
        >
          <Bars3Icon className="size-6 text-gray-700 dark:text-gray-300" />
        </button>

        {sidebarOpen && (
          <div className="fixed inset-0 z-50 flex">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
            <div className="relative w-64 bg-white shadow-2xl dark:bg-gray-800">
              <button
                type="button"
                onClick={() => setSidebarOpen(false)}
                className="absolute top-4 right-4 rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <XMarkIcon className="size-6 text-gray-700 dark:text-gray-300" />
              </button>
              <SidebarContent />
            </div>
          </div>
        )}
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block lg:w-64 lg:flex-shrink-0">
        <div className="fixed h-full w-64 border-r border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <SidebarContent />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 lg:p-8">
          <div className="mx-auto max-w-5xl">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Company Settings</h1>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  Manage your company profile and account preferences
                </p>
              </div>
            
            </div>

            <div className="space-y-6">
              {/* Company Details */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <div className="mb-6 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Company Information</h2>
                  <button
                    type="button"
                    onClick={saveCompanyProfile}
                    disabled={settingsSaving || readWebsiteLoading}
                    className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-lg hover:from-blue-500 hover:to-indigo-500 disabled:opacity-60"
                  >
                    {settingsSaving ? (
                      <span className="flex items-center gap-2">
                        <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Saving...
                      </span>
                    ) : (
                      'Save Changes'
                    )}
                  </button>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Email
                    </label>
                    <input
                      type="email"
                      value={companyForm.Email}
                      onChange={(e) => handleCompanyFieldChange('Email', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Telephone
                    </label>
                    <input
                      type="text"
                      value={companyForm.Telephone}
                      onChange={(e) => handleCompanyFieldChange('Telephone', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={companyForm.CompanyName}
                      onChange={(e) => handleCompanyFieldChange('CompanyName', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Industry
                    </label>
                    <input
                      type="text"
                      value={companyForm.Industry}
                      onChange={(e) => handleCompanyFieldChange('Industry', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Website
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={companyForm.Website}
                        onChange={(e) => handleCompanyFieldChange('Website', e.target.value)}
                        placeholder="https://example.com"
                        className="flex-1 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={handleReadWebsite}
                        disabled={readWebsiteLoading || !companyForm.Website}
                        aria-busy={readWebsiteLoading}
                        className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 px-4 py-2 text-sm font-medium text-white shadow-lg hover:from-emerald-400 hover:to-green-400 disabled:opacity-60"
                      >
                        {readWebsiteLoading ? (
                          <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          <GlobeAltIcon className="size-4" />
                        )}
                        {readWebsiteLoading ? 'Analysing website...' : 'Read Website'}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Contact Name
                    </label>
                    <input
                      type="text"
                      value={companyForm.ContactName}
                      onChange={(e) => handleCompanyFieldChange('ContactName', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Postcode
                    </label>
                    <input
                      type="text"
                      value={companyForm.Postcode}
                      onChange={(e) => handleCompanyFieldChange('Postcode', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Address
                    </label>
                    <input
                      type="text"
                      value={companyForm.Address}
                      onChange={(e) => handleCompanyFieldChange('Address', e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Description
                    </label>
                    <textarea
                      value={companyForm.Description}
                      onChange={(e) => handleCompanyFieldChange('Description', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Value Proposition
                    </label>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Example: "We saved companies 8 hours per week."
                    </p>
                    <textarea
                      value={companyForm.ValueProposition}
                      onChange={(e) => handleCompanyFieldChange('ValueProposition', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Call To Action
                    </label>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Example: "Schedule a demo to see how we can help."
                    </p>
                    <textarea
                      value={companyForm.CallToAction}
                      onChange={(e) => handleCompanyFieldChange('CallToAction', e.target.value)}
                      rows={3}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {settingsError && (
                  <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                    {settingsError}
                  </div>
                )}
                {settingsFeedback && (
                  <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
                    {settingsFeedback}
                  </div>
                )}
              </div>

              {/* Email Footer */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Email Footer</h2>
                <div className="grid gap-6 lg:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      HTML Footer Code
                    </label>
                    <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
                      Paste your email signature HTML here. You can download/export this from Gmail and Outlook.
                    </p>
                    <textarea
                      value={companyForm.EmailFooter || ''}
                      onChange={(e) => handleCompanyFieldChange('EmailFooter', e.target.value)}
                      rows={8}
                      placeholder="Paste HTML footer code..."
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-700">
                      <h4 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                        Live Preview
                      </h4>
                      <div className="min-h-[200px] rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-gray-800">
                        {livePreviewHtml ? (
                          <div dangerouslySetInnerHTML={{ __html: livePreviewHtml }} />
                        ) : (
                          <p className="text-sm text-gray-400 dark:text-gray-500">Paste HTML above to preview.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Change Password */}
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Change Password</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordOne}
                      onChange={(e) => setPasswordOne(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-gray-600 dark:text-gray-400">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={passwordTwo}
                      onChange={(e) => setPasswordTwo(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                {passwordError && (
                  <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                    {passwordError}
                  </div>
                )}
                {passwordFeedback && (
                  <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-400">
                    {passwordFeedback}
                  </div>
                )}

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={savePasswordChange}
                    disabled={passwordSaving}
                    className="rounded-lg bg-gradient-to-r from-gray-700 to-gray-900 px-4 py-2 text-sm font-medium text-white shadow-lg hover:from-gray-600 hover:to-gray-800 disabled:opacity-60 dark:from-gray-600 dark:to-gray-800"
                  >
                    {passwordSaving ? (
                      <span className="flex items-center gap-2">
                        <span className="size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Changing...
                      </span>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Sidebar Component ──
function SidebarContent() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 shrink-0 items-center px-6">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-600">
            <img src="/avenor.png" alt="Logo" className="size-8 rounded-lg" />
          </div>
          <span className="text-lg font-bold text-gray-900 dark:text-white">Avenor AI</span>
        </div>
      </div>
      
      <nav className="flex-1 px-4 pb-4">
        <ul className="space-y-1">
          {navigation.map((item) => (
            <li key={item.name}>
              <button
                onClick={() => navigate(item.href)}
                className={classNames(
                  location.pathname === item.href
                    ? 'bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 dark:from-blue-900/20 dark:to-indigo-900/20 dark:text-blue-400'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50',
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors'
                )}
              >
                <item.icon
                  className={classNames(
                    location.pathname === item.href
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300',
                    'size-5 shrink-0'
                  )}
                />
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>

      
  );
}